import { useSelector } from 'react-redux';
import {
  selectCurrentAdmin,
  selectPlatformRoles
} from '../../redux/auth/selectors';

/**
 * Current identity and platform capabilities, resolved once by the session layer.
 *
 * `/me` reports the platform plane as `["PLATFORM_OWNER"]`, `["SUPPORT_ADMIN"]` or `[]`.
 * The roles are compared upper-cased, so a spelling change on the wire cannot lock the owner
 * out or, worse, read a support admin as one.
 *
 * `canWritePlatform` is the PLATFORM_OWNER and nobody else: a support admin reads the
 * console and changes nothing, and the server enforces that per route. Every write control
 * in the console hangs off this flag, so none is drawn for a role that would only be refused.
 */
export const useCurrentUser = () => {
  const user = useSelector(selectCurrentAdmin) || {};
  const platformRoles: string[] = useSelector(selectPlatformRoles);
  const isPlatformOwner = platformRoles.includes('PLATFORM_OWNER');
  const isSupportAdmin = !isPlatformOwner && platformRoles.includes('SUPPORT_ADMIN');
  return {
    user,
    platformRoles,
    isPlatformOwner,
    isSupportAdmin,
    canAccessPlatformAdmin: isPlatformOwner || isSupportAdmin,
    canWritePlatform: isPlatformOwner
  };
};
